import React, { useState, useCallback } from "react";
import {
  FaVolumeUp,
  FaStar,
  FaRedo,
  FaTrophy,
  FaGraduationCap,
  FaQuestionCircle,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaSmile,
  FaMicrophone,
  FaUser,
  FaEdit
} from "react-icons/fa";
import "./BodyPartsGame.css";

// Image import
import bodyPartsImg from "./body-parts-for-kids.jpg";

// Re-calibrated hotspot coordinates
const BODY_PARTS = [
  { id: "head", label: "Head", prompt: "Tap on the Head!", x: 19, y: 22.5 },
  { id: "hair", label: "Hair", prompt: "Where is the Hair?", x: 79, y: 22.8 },
  { id: "eyebrow", label: "Eyebrow", prompt: "Can you find the Eyebrow?", x: 24, y: 28 },
  { id: "eye", label: "Eye", prompt: "Touch the Eye!", x: 79, y: 31 },
  { id: "ear", label: "Ear", prompt: "Where is the Ear?", x: 23, y: 33.5 },
  { id: "nose", label: "Nose", prompt: "Point to the Nose!", x: 23, y: 39 },
  { id: "mouth", label: "Mouth", prompt: "Can you find the Mouth?", x: 79, y: 35.5 },
  { id: "chin", label: "Chin", prompt: "Where is the Chin?", x: 79, y: 41 },
  { id: "neck", label: "Neck", prompt: "Touch the Neck!", x: 23, y: 45.5 },
  { id: "shoulder", label: "Shoulder", prompt: "Tap on the Shoulder!", x: 42, y: 43 },
  { id: "chest", label: "Chest", prompt: "Where is the Chest?", x: 79, y: 47 },
  { id: "arm", label: "Arm", prompt: "Point to the Arm!", x: 23, y: 52.8 },
  { id: "elbow", label: "Elbow", prompt: "Can you find the Elbow?", x: 38.5, y: 54 },
  { id: "stomach", label: "Stomach", prompt: "Touch the Stomach!", x: 73.5, y: 53.5 },
  { id: "finger", label: "Finger", prompt: "Where are the Fingers?", x: 21.5, y: 66.5 },
  { id: "thumb", label: "Thumb", prompt: "Point to the Thumb!", x: 75, y: 62 },
  { id: "knee", label: "Knee", prompt: "Touch the Knee!", x: 73, y: 76 },
  { id: "leg", label: "Leg", prompt: "Can you find the Leg?", x: 23.5, y: 81.8 },
  { id: "toe", label: "Toe", prompt: "Where are the Toes?", x: 21.5, y: 92.2 },
  { id: "foot", label: "Foot", prompt: "Point to the Foot!", x: 74, y: 89.8 }
];

const DEFAULT_NAME = "Little Learner";

function BodyPartsGame() {
  const [userName, setUserName] = useState(DEFAULT_NAME);
  const [tempNameInput, setTempNameInput] = useState("");
  const [showNameModal, setShowNameModal] = useState(true);
  const [isListeningForName, setIsListeningForName] = useState(false);

  const [gameMode, setGameMode] = useState("explore"); // "explore" | "quiz"
  const [activeTarget, setActiveTarget] = useState(null);
  const [hoveredPart, setHoveredPart] = useState(null);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState({ text: "Hover or tap on any part to learn!", type: "info" });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [completedQuiz, setCompletedQuiz] = useState(false);

  // New state to manage answer visual feedback in quiz mode
  const [quizFeedbackPart, setQuizFeedbackPart] = useState(null); // part object
  const [quizFeedbackType, setQuizFeedbackType] = useState(null); // "correct" | "wrong"

  // Web Speech Synthesis
  const speakText = useCallback((text) => {
    if (!("speechSynthesis" in window)) return;

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.88;
    utterance.pitch = 1.1;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  }, []);

  // Save Name Handler
  const handleSaveName = (nameToSet) => {
    const finalName = nameToSet && nameToSet.trim() !== "" ? nameToSet.trim() : DEFAULT_NAME;
    setUserName(finalName);
    setShowNameModal(false);

    const welcomeMsg = `Welcome, ${finalName}! Hover over or tap any part to explore!`;
    setFeedback({ text: welcomeMsg, type: "info" });
    speakText(welcomeMsg);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSaveName(tempNameInput);
  };

  // Voice Input Recognition for Name
  const handleListenName = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition is not supported on this browser. Please type your name!");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;

    recognition.onstart = () => setIsListeningForName(true);
    recognition.onend = () => setIsListeningForName(false);
    recognition.onerror = () => setIsListeningForName(false);

    recognition.onresult = (event) => {
      const spokenName = event.results[0][0].transcript;
      if (spokenName) {
        setTempNameInput(spokenName);
        handleSaveName(spokenName);
      }
    };

    recognition.start();
  };

  // Next Quiz Prompt Generator
  const nextQuizQuestion = useCallback(() => {
    const randomPart = BODY_PARTS[Math.floor(Math.random() * BODY_PARTS.length)];
    setActiveTarget(randomPart);
    setQuizFeedbackPart(null);
    setQuizFeedbackType(null);

    const msg = `${userName}, ${randomPart.prompt}`;
    setFeedback({ text: msg, type: "prompt" });
    speakText(msg);
  }, [speakText, userName]);

  // Handle Game Mode Switch
  const handleModeChange = (mode) => {
    setGameMode(mode);
    setScore(0);
    setCompletedQuiz(false);
    setQuizFeedbackPart(null);
    setQuizFeedbackType(null);

    if (mode === "quiz") {
      nextQuizQuestion();
    } else {
      setActiveTarget(null);
      const msg = `Explore Mode! Hover or click on any hotspot to learn, ${userName}.`;
      setFeedback({ text: msg, type: "info" });
      speakText(msg);
    }
  };

  // Hotspot / Chip Click Action
  const handlePartClick = (part) => {
    if (gameMode === "explore") {
      setActiveTarget(part);
      const speech = `This is the ${part.label}`;
      setFeedback({ text: speech, type: "success" });
      speakText(speech);
    } else if (gameMode === "quiz") {
      if (part.id === activeTarget?.id) {
        // Correct Choice
        setQuizFeedbackPart(part);
        setQuizFeedbackType("correct");

        const newScore = score + 1;
        setScore(newScore);

        if (newScore >= 5) {
          setCompletedQuiz(true);
          const winMsg = `Hooray ${userName}! You successfully found 5 body parts!`;
          setFeedback({ text: winMsg, type: "celebrate" });
          speakText(winMsg);
        } else {
          const successMsg = `Great job ${userName}! That is the ${part.label}!`;
          setFeedback({ text: successMsg, type: "success" });
          speakText(successMsg);
          setTimeout(() => nextQuizQuestion(), 1800);
        }
      } else {
        // Wrong Choice
        setQuizFeedbackPart(part);
        setQuizFeedbackType("wrong");

        const errorMsg = `Oops ${userName}! That's the ${part.label}. Try again! ${activeTarget.prompt}`;
        setFeedback({ text: errorMsg, type: "error" });
        speakText(errorMsg);

        // Reset wrong visual state after 1.5s
        setTimeout(() => {
          setQuizFeedbackPart(null);
          setQuizFeedbackType(null);
        }, 1500);
      }
    }
  };

  // Hover Actions for Explore Mode
  const handleMouseEnter = (part) => {
    if (gameMode === "explore") {
      setHoveredPart(part);
      speakText(part.label);
    }
  };

  const handleMouseLeave = () => {
    setHoveredPart(null);
  };

  return (
    <div className="body-game-wrapper fade-in">
      {/* Header Bar */}
      <header className="game-header">
        <div className="game-title-group">
          <h2>
            <FaGraduationCap className="header-icon" /> Interactive Body Explorer
          </h2>
          <div className="user-profile-tag">
            <FaUser className="user-icon" />
            <span>Learner: <strong>{userName}</strong></span>
            <button
              type="button"
              className="edit-name-btn"
              onClick={() => setShowNameModal(true)}
              title="Change Name"
            >
              <FaEdit />
            </button>
          </div>
        </div>

        <button
          type="button"
          className={`voice-speaker-btn ${isSpeaking ? "pulse-speaking" : ""}`}
          onClick={() => speakText(feedback.text)}
        >
          <FaVolumeUp className="speaker-icon" />
          <span>{isSpeaking ? "AI Speaking..." : "Listen Voice"}</span>
        </button>
      </header>

      {/* Mode Selector */}
      <div className="game-mode-nav">
        <button
          type="button"
          className={`mode-btn ${gameMode === "explore" ? "active" : ""}`}
          onClick={() => handleModeChange("explore")}
        >
          <FaSearch /> Explore & Learn Mode
        </button>
        <button
          type="button"
          className={`mode-btn ${gameMode === "quiz" ? "active" : ""}`}
          onClick={() => handleModeChange("quiz")}
        >
          <FaQuestionCircle /> AI Voice Quiz
        </button>
      </div>

      {/* Dynamic Voice Banner */}
      <div className={`feedback-banner banner-${feedback.type}`}>
        {feedback.type === "success" && <FaCheckCircle className="status-icon" />}
        {feedback.type === "error" && <FaTimesCircle className="status-icon" />}
        {feedback.type === "celebrate" && <FaTrophy className="status-icon gold" />}
        <span className="feedback-text">{feedback.text}</span>
      </div>

      {/* Quiz Progress Tracker */}
      {gameMode === "quiz" && (
        <div className="score-tracker-bar">
          <span className="score-label">Stars Earned:</span>
          <div className="stars-row">
            {[...Array(5)].map((_, i) => (
              <FaStar key={i} className={`star-icon ${i < score ? "earned" : ""}`} />
            ))}
          </div>
          <button type="button" className="reset-btn" onClick={() => handleModeChange("quiz")}>
            <FaRedo /> Restart
          </button>
        </div>
      )}

      {/* Main Interactive Anatomy Stage */}
      <div className="game-stage">
        <div className="illustration-container">
          <div className="character-body-box">
            
            {/* Image Diagram */}
            <img
              src={bodyPartsImg}
              alt="My Body Diagram"
              className="vector-character-svg"
            />

            {/* Interactive Hotspots layered on top */}
            {BODY_PARTS.map((part) => {
              const isHovered = hoveredPart?.id === part.id;
              const isSelectedExplore = gameMode === "explore" && activeTarget?.id === part.id;
              const isQuizFeedback = gameMode === "quiz" && quizFeedbackPart?.id === part.id;

              // Tooltip only shows during explore hover/select or when tapped in quiz mode
              const shouldShowLabel =
                (gameMode === "explore" && (isHovered || isSelectedExplore)) ||
                (gameMode === "quiz" && isQuizFeedback);

              // Dynamic CSS classes for answer state styling
              let quizStateClass = "";
              if (isQuizFeedback) {
                quizStateClass = quizFeedbackType === "correct" ? "quiz-correct" : "quiz-wrong";
              }

              return (
                <button
                  key={part.id}
                  type="button"
                  className={`hotspot-btn ${isSelectedExplore ? "selected-target" : ""} ${quizStateClass}`}
                  style={{ left: `${part.x}%`, top: `${part.y}%` }}
                  onClick={() => handlePartClick(part)}
                  onMouseEnter={() => handleMouseEnter(part)}
                  onMouseLeave={handleMouseLeave}
                  aria-label={part.label}
                >
                  <span className="hotspot-dot" />
                  {shouldShowLabel && (
                    <span className={`hotspot-tooltip ${quizStateClass}`}>
                      {part.label}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick Selection Chip Panel */}
        <div className="quick-buttons-panel">
          <h3>
            <FaSmile /> Quick Tap List
          </h3>
          <p className="panel-sub">Tap any label below to select and learn:</p>
          <div className="tags-grid">
            {BODY_PARTS.map((part) => {
              const isChipActiveExplore = gameMode === "explore" && activeTarget?.id === part.id;
              const isQuizFeedback = gameMode === "quiz" && quizFeedbackPart?.id === part.id;

              let chipQuizClass = "";
              if (isQuizFeedback) {
                chipQuizClass = quizFeedbackType === "correct" ? "chip-correct" : "chip-wrong";
              }

              return (
                <button
                  key={part.id}
                  type="button"
                  className={`tag-chip ${isChipActiveExplore ? "chip-active" : ""} ${chipQuizClass}`}
                  onClick={() => handlePartClick(part)}
                  onMouseEnter={() => handleMouseEnter(part)}
                  onMouseLeave={handleMouseLeave}
                >
                  {part.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Onboarding Name Modal */}
      {showNameModal && (
        <div className="game-modal-overlay">
          <form className="game-modal-card fade-in" onSubmit={handleFormSubmit}>
            <div className="modal-icon-header">
              <FaSmile className="welcome-avatar-icon" />
            </div>
            <h2>Welcome, Little Explorer!</h2>
            <p>What is your name? Enter it below or speak into your microphone.</p>

            <div className="name-input-group">
              <input
                type="text"
                placeholder="Enter your name..."
                value={tempNameInput}
                onChange={(e) => setTempNameInput(e.target.value)}
                className="name-text-input"
                autoFocus
              />
              <button
                type="button"
                className={`mic-voice-btn ${isListeningForName ? "listening" : ""}`}
                onClick={handleListenName}
              >
                <FaMicrophone />
                <span>{isListeningForName ? "Listening..." : "Speak Name"}</span>
              </button>
            </div>

            <div className="modal-actions">
              <button
                type="submit"
                className="start-game-btn"
              >
                Start Exploring!
              </button>
              <button
                type="button"
                className="skip-btn"
                onClick={() => handleSaveName(DEFAULT_NAME)}
              >
                Continue as "Little Learner"
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Celebration Win Popup */}
      {completedQuiz && (
        <div className="game-modal-overlay">
          <div className="game-modal-card win-card fade-in">
            <FaTrophy className="big-gold-trophy" />
            <h2>Fantastic Job, {userName}!</h2>
            <p>You earned 5 stars and mastered preschool anatomy!</p>
            <div className="win-stars">
              <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
            </div>
            <button
              type="button"
              className="start-game-btn"
              onClick={() => handleModeChange("quiz")}
            >
              Play Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BodyPartsGame;